/**
 * Searching jobs: the client is browsing experts before deciding to hire.
 * Experts show interest with their portfolio instead of a written application.
 */
export const APPLICATION_TYPE_INTEREST = 'interest';

// Sent as the application message when the expert adds no note. Varied so
// clients don't see the same line from everyone. Keep in sync with
// Message::INTEREST_MESSAGES in the API.
export const INTEREST_MESSAGES = [
  'Hi, I’m interested in this job. Please take a look at my portfolio.',
  'Hi, this job caught my eye. Have a look at my portfolio to see the kind of work I do.',
  'Hello, I’d like to be considered for this job. My portfolio shows what I’ve worked on.',
  'Hi, I’m interested in this one. If my portfolio fits what you need, message me and we can talk.',
  'Hi, I saw your job and I’m interested. You can see my work in my portfolio.',
  'Hi, I’d be glad to help with this job. Take a look at my portfolio when you have time.',
  'Hello, this sounds like a job I’d enjoy. My portfolio is a good place to start.',
  'Hi, I’m interested in working with you on this. Please have a look at my portfolio.',
  'Hi, I’d like to put myself forward for this job. My portfolio gives a good sense of my work.',
  'Hello, I’d like to hear more about this job. Feel free to look through my portfolio first.',
  'Hi, I’m interested in this job and happy to answer any questions. My portfolio shows the work I do.',
  'Hi, this looks like an interesting job. If you like what you see in my portfolio, send me a message.',
  'Hello, I’d like to hear more about what you have in mind. My portfolio will give you an idea of my work.',
  'Hi, I’m interested. My portfolio shows the kind of projects I take on.',
  'Hi, I’d like to be on your list for this job. Please take a look at my portfolio.',
  'Hello, I came across your job and I’m interested. Have a browse through my portfolio.',
  'Hi, count me in for this job. You’ll find examples of my work in my portfolio.',
  'Hi, if you’re still looking at experts for this job, I’m interested. My portfolio shows my work.',
  'Hello, I’m interested in this job. If my portfolio looks like a fit, I’d be happy to talk it through.',
  'Hi, I’d be interested in working on this. Message me if you’d like to talk after seeing my portfolio.',
];

/**
 * The same expert gets the same line on a job (and the API picks it the same
 * way), so the preview matches what's sent and doesn't change on reload.
 *
 * @param {number|string} briefId
 * @param {number|string} userId
 * @returns {string}
 */
export function getDefaultInterestMessage(briefId, userId) {
  const index =
    ((Number(briefId) || 0) + (Number(userId) || 0)) % INTEREST_MESSAGES.length;
  return INTEREST_MESSAGES[index];
}

// A note shorter than this isn't auto-approved. Keep in sync with
// MIN_INTEREST_PITCH_CHARS in the API's AutoActionPendingApprovalPitch.
export const MIN_INTEREST_NOTE_CHARS = 10;

/**
 * An empty note is fine (the default message is sent), but a written one
 * needs enough to be worth reading.
 *
 * @param {string} note
 * @returns {boolean}
 */
export function isInterestNoteTooShort(note) {
  const length = Array.from(String(note || '').trim()).length;
  return length > 0 && length < MIN_INTEREST_NOTE_CHARS;
}

export const SEARCHING_TOOLTIP =
  'The client is browsing experts and hasn’t decided to hire yet.';

/**
 * @param {object} brief
 * @returns {boolean}
 */
export function isSearchingJob(brief) {
  return Boolean(brief) && brief.hiring_intent === 'browse';
}
